/**
 * @author Vivek Kumar <vivek.kumar@314ecorp.com>
 * @description React Hook useUploadAttachmentFlow
 */
import { useCallback, useMemo } from 'react';

import useUploadAttachments from 'hooks/claim/mutate/useUploadAttachments';
import { useUploadManagerActions } from 'store/uploadManager';
import AppEvents, { Events } from '@veritable/common/utils/appEvents';
import {
	DEFAULT_CONTENT_TYPE,
	getUploadErrorMessage,
	normalizeAcceptedExtensions,
	partitionAttachmentFiles,
	uploadFileToPresignedUrl,
} from 'utils/uploadAttachment';

interface UseUploadAttachmentFlowParams {
	entityId: string;
	entityType: EntityTypeEnum;
	description?: string;
	acceptedExtensions?: string[];
	fileSizeLimitMB?: number;
}

interface UseUploadAttachmentFlowResult {
	uploadFiles: (files: File[]) => Promise<void>;
}

const useUploadAttachmentFlow = ({
	entityId,
	entityType,
	description,
	acceptedExtensions = [],
	fileSizeLimitMB = Number.POSITIVE_INFINITY,
}: UseUploadAttachmentFlowParams): UseUploadAttachmentFlowResult => {
	const { mutateAsync: registerAttachment } = useUploadAttachments();
	const { startUpload, updateUpload } = useUploadManagerActions();
	const extensions = useMemo(() => normalizeAcceptedExtensions(acceptedExtensions), [acceptedExtensions]);

	const uploadAttachmentFile = useCallback(
		async (file: File, presignedUrl: string, uploadId: string) => {
			updateUpload(uploadId, { status: 'uploading', progress: 0 });

			await uploadFileToPresignedUrl({
				file,
				presignedUrl,
				onProgress: (progress) => {
					updateUpload(uploadId, { status: 'uploading', progress });
				},
			});

			updateUpload(uploadId, {
				status: 'completed',
				progress: 100,
				completedAt: new Date().toISOString(),
			});
		},
		[updateUpload],
	);

	const uploadFiles = useCallback(
		async (files: File[]) => {
			if (files.length === 0) {
				return;
			}

			const { validFiles, errorMessages } = partitionAttachmentFiles(files, extensions, fileSizeLimitMB);
			errorMessages.forEach((message) => AppEvents.publish(Events.Error, message));

			if (validFiles.length === 0) {
				return;
			}

			const uploadIds = validFiles.map((file) => startUpload({ fileName: file.name, size: file.size, entityId, entityType }));

			uploadIds.forEach((id) => updateUpload(id, { status: 'registering' }));

			try {
				const uploadResponses = await registerAttachment({
					entityType,
					entityId,
					files: validFiles.map((file) => ({ fileName: file.name, fileType: file.type || DEFAULT_CONTENT_TYPE })),
					description,
				});

				if (uploadResponses.length !== validFiles.length) {
					throw new Error('Mismatch between files and presigned URL responses');
				}

				const results = await Promise.allSettled(
					validFiles.map((file, i) => {
						const presignedUrl = uploadResponses[i]?.presignedUrl;
						if (!presignedUrl) {
							return Promise.reject(new Error(`Upload details missing for ${file.name}`));
						}
						return uploadAttachmentFile(file, presignedUrl, uploadIds[i]);
					}),
				);

				const successfulFiles: File[] = [];
				results.forEach((result, i) => {
					if (result.status === 'fulfilled') {
						successfulFiles.push(validFiles[i]);
					} else {
						const errorMessage = getUploadErrorMessage(result.reason, validFiles[i].name);
						updateUpload(uploadIds[i], { status: 'failed', error: errorMessage });
						AppEvents.publish(Events.Error, errorMessage);
					}
				});

				if (successfulFiles.length > 0) {
					AppEvents.publishApiSuccess(
						successfulFiles.length === 1
							? `${successfulFiles[0].name} uploaded successfully`
							: `${successfulFiles.length} files uploaded successfully`,
					);
				}
			} catch (error) {
				uploadIds.forEach((id, i) => {
					const errorMessage = getUploadErrorMessage(error, validFiles[i].name);
					updateUpload(id, { status: 'failed', error: errorMessage });
				});
				AppEvents.publish(Events.Error, getUploadErrorMessage(error, 'files'));
			}
		},
		[
			description,
			entityId,
			entityType,
			extensions,
			fileSizeLimitMB,
			registerAttachment,
			startUpload,
			updateUpload,
			uploadAttachmentFile,
		],
	);

	return { uploadFiles };
};

export default useUploadAttachmentFlow;
export type { UseUploadAttachmentFlowParams, UseUploadAttachmentFlowResult };
